import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight } from 'lowlight';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

// Create lowlight instance with common languages
const lowlight = createLowlight();

export function RichTextEditor({ content, onChange, placeholder }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We'll use CodeBlockLowlight instead
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CodeBlockLowlight.configure({
        lowlight,
        languageClassPrefix: 'language-',
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary-500 hover:text-primary-600 underline',
        },
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none p-4 min-h-[200px] focus:outline-none',
        'data-placeholder': placeholder || 'Start typing...',
      },
    },
  });

  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      {/* Enhanced Toolbar */}
      <div className="border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 p-3">
        <div className="flex flex-wrap gap-1">
          {/* Text Formatting */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-bold ${
                editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Bold (Ctrl+B)"
            >
              B
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors italic ${
                editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Italic (Ctrl+I)"
            >
              I
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors underline ${
                editor.isActive('underline') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Underline (Ctrl+U)"
            >
              U
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Headers */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-bold ${
                editor.isActive('heading', { level: 1 }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-bold ${
                editor.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-bold ${
                editor.isActive('heading', { level: 3 }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Heading 3"
            >
              H3
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-bold ${
                editor.isActive('heading', { level: 4 }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Heading 4"
            >
              H4
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Lists */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Bulleted List"
            >
              •
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Numbered List"
            >
              1.
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Text Alignment */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Align Left"
            >
              ⬅
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Align Center"
            >
              ↔
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Align Right"
            >
              ➡
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Links and Media */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={setLink}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Insert/Edit Link"
            >
              🔗
            </button>
            <button
              type="button"
              onClick={addImage}
              className="px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300"
              title="Insert Image"
            >
              🖼
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Code */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('code') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Inline Code"
            >
              &lt;/&gt;
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('codeBlock') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Code Block"
            >
              { }
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Quote and Divider */}
          <div className="flex gap-1 mr-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                editor.isActive('blockquote') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
              }`}
              title="Blockquote"
            >
              " "
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              className="px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300"
              title="Horizontal Line"
            >
              —
            </button>
          </div>

          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Undo/Redo */}
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              className="px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              ↶
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              className="px-3 py-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              ↷
            </button>
          </div>
        </div>
      </div>
      <EditorContent
        editor={editor}
        className="focus:outline-none"
      />
    </div>
  );
}
